// GET /admin/reports/transactional — with ?org_id= for one business, or
// omitted entirely for the platform-wide view.
export interface TransactionalSummary {
  total: number;
  queued: number;
  sent: number;
  delivered: number;
  failed: number;
  simulated: number;
  delivered_rate: number;
  failed_rate: number;
  total_cost: number;
  live_count: number;
  test_count: number;
  currency: string;
  currency_symbol: string;
}

export interface TransactionalMessage {
  message_id: string;
  org_id?: string;
  org_name?: string;
  to: string;
  sender_id: string;
  message: string;
  status: string;
  key_type: string;
  batch_id: string | null;
  gateway_message_id: string | null;
  err_code: string | null;
  cost: number;
  billing_event_id: string | null;
  api_key_name: string | null;
  created_at: string;
  updated_at: string;
}

interface PaginatedMessages {
  page: number;
  limit: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  data: TransactionalMessage[];
}

export interface TransactionalBatch {
  batch_id: string;
  org_id?: string;
  org_name?: string;
  sender_id: string;
  total: number;
  delivered: number;
  sent: number;
  failed: number;
  delivered_rate: number;
  total_cost: number;
  created_at: string;
  currency: string;
  currency_symbol: string;
}

export interface WalletTransaction {
  id: string;
  source: string;
  transaction_type: string;
  amount: number;
  currency: string;
  created_at: string;
}

export interface TransactionalReportResponse {
  status: boolean;
  org_id: string | null;
  summary: TransactionalSummary;
  messages: PaginatedMessages;
  batches: TransactionalBatch[];
  wallet: {
    currency: string;
    currency_symbol: string;
    total_spent: number;
    transactions: WalletTransaction[];
    count: number;
  };
  filters: Record<string, unknown>;
}

// Filters the endpoint accepts as query params — all optional.
export interface TransactionalReportFilters {
  org_id?: string;
  status?: string;
  key_type?: string;
  sender_id?: string;
  search?: string;
  batch_id?: string;
  start_date?: string;
  end_date?: string;
  transaction_type?: string;
}

export const MESSAGE_STATUS_OPTIONS = ["queued", "sent", "delivered", "failed", "simulated"] as const;
export const KEY_TYPE_OPTIONS = ["live", "test"] as const;

export const MESSAGE_STATUS_STYLES: Record<string, string> = {
  delivered: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  sent: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  queued: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  failed: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
  simulated: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

export const KEY_TYPE_STYLES: Record<string, string> = {
  live: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400",
  test: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

export const formatReportDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
